import { ENV, SUPABASE_URL, SUPABASE_ANON_KEY } from '../config/env';
import { supabase } from './supabaseClient';

export const REVIEW_LINK = 'https://app.roadserve.in/go/review';
const DEFAULT_ACCOUNT_ID = 'ab37ce64-bdb9-46ff-b482-7f2af00b73d5';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
});

function phone10(value) {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
}

function firstName(name) {
  const part = String(name || 'Customer').trim().split(/\s+/)[0];
  return part || 'Customer';
}

function placeholderCount(text) {
  const nums = [...String(text || '').matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1]));
  return nums.length ? Math.max(...nums) : 0;
}

function getWhatsAppUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    if (user?.id && String(user.id).length > 20) return user.id;
  } catch {
    /* ignore */
  }
  return ENV.WHATSAPP_SYSTEM_USER_ID || null;
}

async function resolveRoadServeAccountId() {
  try {
    const { data } = await supabase
      .from('whatsapp_accounts_safe')
      .select('id, name, display_phone, is_active')
      .eq('is_active', true);
    const rows = Array.isArray(data) ? data : [];
    const digits = (v) => String(v || '').replace(/\D/g, '');
    const preferred =
      rows.find((a) => digits(a.display_phone).endsWith('7909999427')) ||
      rows.find((a) => String(a.name || '').trim() === 'RoadServe');
    if (preferred?.id) return preferred.id;
  } catch {
    /* fallback */
  }
  return DEFAULT_ACCOUNT_ID;
}

async function loadAskReviewTemplate(accountId) {
  const wanted = String(ENV.ASK_REVIEW_WA_TEMPLATE || 'ask_review').toLowerCase();
  const url = accountId
    ? `${SUPABASE_URL}/functions/v1/whatsapp-templates?account_id=${accountId}`
    : `${SUPABASE_URL}/functions/v1/whatsapp-templates`;
  const res = await fetch(url, { headers: authHeaders() });
  const json = await res.json().catch(() => ({}));
  const list = (json.templates || []).filter((t) => String(t.status || '').toUpperCase() === 'APPROVED');
  return (
    list.find((t) => String(t.name || '').toLowerCase() === wanted) ||
    list.find((t) => /ask_review|customer_review|review_request/.test(String(t.name || '').toLowerCase())) ||
    null
  );
}

function buildParams(tpl, row) {
  const name = firstName(row.customer_name);
  const service = String(row.service_type || row.service_name || 'RoadServe').trim() || 'RoadServe';
  const pool = [name, REVIEW_LINK, service];
  const n = placeholderCount(tpl?.body_text);
  return pool.slice(0, n);
}

function buildButtonParams(tpl, phone) {
  const buttons = Array.isArray(tpl?.buttons) ? tpl.buttons : [];
  const urlButtons = buttons.filter(
    (b) => String(b?.type || '').toUpperCase() === 'URL' && /\{\{\d+\}\}/.test(String(b?.url || '')),
  );
  if (!urlButtons.length) return [];
  return urlButtons.flatMap((btn) => {
    const count = (String(btn.url || '').match(/\{\{\d+\}\}/g) || []).length;
    const wantsPhone = /[?&]p=\{\{/.test(String(btn.url || ''));
    return Array.from({ length: count }, () => (wantsPhone && phone ? phone : 'review'));
  });
}

async function stampReviewAsked(requestId) {
  if (!requestId) return;
  try {
    await supabase
      .from('service_requests')
      .update({ review_asked_at: new Date().toISOString() })
      .eq('id', requestId);
  } catch {
    /* column may not exist until SQL is run */
  }
}

export async function sendAskReviewTemplate(row) {
  const phone = phone10(row?.customer_phone || row?.contact_number || row?.phone);
  if (!phone) return { success: false, error: 'Customer phone missing' };

  const accountId = await resolveRoadServeAccountId();
  const tpl = await loadAskReviewTemplate(accountId);
  if (!tpl) {
    return {
      success: false,
      error: 'Approved WhatsApp template ask_review nahi mili. WhatsApp → Templates mein create/approve karo.',
    };
  }

  const userId = getWhatsAppUserId();
  const bodyParams = buildParams(tpl, row);
  const buttonParams = buildButtonParams(tpl, phone);
  const payload = {
    to: `91${phone}`,
    type: 'template',
    template_name: tpl.name,
    template_language: tpl.language || ENV.ASK_REVIEW_WA_TEMPLATE_LANG || 'en',
    template_params: bodyParams,
    template_button_params: buttonParams,
    content: `${firstName(row.customer_name)}, review: ${REVIEW_LINK}`,
    contact_name: row.customer_name || 'Customer',
    user_id: userId,
    account_id: accountId,
    source: 'human',
  };

  const res = await fetch(`${SUPABASE_URL}/functions/v1/whatsapp-send`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.success === false) {
    return { success: false, error: json?.error || `Template send failed (${res.status})` };
  }

  await stampReviewAsked(row.id);
  return { success: true, template: tpl.name, phone };
}
